import { listActiveRetrievalAgentEntriesForUsecase } from './retrievalAgentProgress';

// Final ``status`` values of a ``sessions/{usecase_id}`` row (backend
// ``app/utils/run_status.py``) and of a generation job.
const FINISHED_RUN_STATUSES = new Set(['completed', 'failed', 'cancelled']);

// Keys the WebSocket adds to ``updateGenerationProgress`` when the job set them
// (``GENERATION_PROGRESS_OPTIONAL_KEYS`` in ``optuneutils/events/ui_update.py``).
const GENERATION_OPTIONAL_KEYS = ['generation_type', 'generated_rows', 'skipped_rows', 'skipped_reasons', 'summary'];

function sessionsOf(result) {
    return Array.isArray(result?.sessions) ? result.sessions : [];
}

/** Epoch ms of a backend timestamp; a naive one is UTC, as the backend writes them. */
export function serverTime(value) {
    if (!value) return null;
    let text = String(value).replace(' ', 'T').replace(/(\.\d{3})\d+/, '$1');
    if (!/(Z|[+-]\d{2}:?\d{2})$/.test(text)) text += 'Z';
    const parsed = Date.parse(text);
    return Number.isNaN(parsed) ? null : parsed;
}

/** When a generation job was created, from its ObjectId: epoch ms, to the second. */
function jobCreatedAt(job) {
    return /^[0-9a-f]{24}$/i.test(job?.job_id || '') ? parseInt(job.job_id.slice(0, 8), 16) * 1000 : null;
}

/**
 * True when *job* can be an upload's label run: created after the upload started (*uploadStartedAt*, epoch ms).
 * A re-upload reuses its dataset name, so until its own job exists the newest one is the previous upload's.
 */
function startedAfterUpload(job, uploadStartedAt) {
    const created = jobCreatedAt(job);
    // An ObjectId keeps whole seconds, so a job created in the upload's own second reads up to 1 s early.
    return uploadStartedAt != null && created != null && created >= uploadStartedAt - 1000;
}

function isFinished(entry) {
    return Boolean(entry?.isCompleted || entry?.isError);
}

/**
 * True while *entry* shows a generation run that has not ended: a
 * Generate-with-AI card, or an upload, clone or Retrieval Agent card on its
 * label or trajectory generation step.
 */
function isGenerationInFlight(entry) {
    if (!entry?.usecaseId || isFinished(entry)) return false;
    return Boolean(entry.isGeneration || (entry.isLabelGeneration && entry.phase === 'label_generation'));
}

/** Datasets a live event for the card can name: a library clone's card is keyed by the clone. */
function datasetNamesOf([key, entry]) {
    const ownKey = entry.isGeneration || entry.isRetrievalAgent ? null : key;
    return [...new Set([ownKey, entry.datasetName].filter(Boolean))];
}

/**
 * The cards *progressStatus* shows, by dataset and by Retrieval Agent session. Taken when a request starts
 * and again when it answers, they tell a card removed meanwhile (dismissed, or ended and cleared) or taken
 * over by a re-upload of its dataset, which the answer must not touch, from one never shown.
 */
export function shownProgressCards(progressStatus) {
    const cardsByDataset = new Map();
    const sessions = new Set();
    for (const card of Object.entries(progressStatus || {})) {
        const [key, entry] = card;
        if (!entry) continue;
        // An upload's status id tells a re-upload from the upload whose card it took over.
        const identity = entry.uploadStatusId || key;
        for (const name of datasetNamesOf(card)) {
            cardsByDataset.set(name, [...(cardsByDataset.get(name) || []), identity]);
        }
        if (entry.sessionId) sessions.add(entry.sessionId);
    }
    const datasets = new Map([...cardsByDataset].map(([name, ids]) => [name, ids.sort().join('|')]));
    return { datasets, sessions };
}

/** True when *datasetName* had cards at *before* (a `shownProgressCards` reading) and they changed by *after*. */
export function datasetCardsChanged(before, after, datasetName) {
    return before.datasets.has(datasetName) && before.datasets.get(datasetName) !== after.datasets.get(datasetName);
}

/** Generation runs still in flight locally, as [progress key, entry] pairs. */
export function inFlightGenerationCards(progressStatus) {
    return Object.entries(progressStatus || {}).filter(([, entry]) => isGenerationInFlight(entry));
}

/**
 * True while the card that was *original* still shows that run unfinished: the
 * same upload (by its status id), else a generation still in flight. While a
 * request is pending, the card can end live, be dismissed, or be reused by
 * another upload.
 */
export function isSameUnfinishedGeneration(current, original) {
    if (original?.uploadStatusId) {
        return current?.uploadStatusId === original.uploadStatusId && !isFinished(current);
    }
    return isGenerationInFlight(current);
}

/** One GET ``ai-generation/jobs/{usecase_id}`` per task of *cards*, as `{ usecaseId, path, cards }`. */
export function generationJobRequests(cards) {
    const cardsByUsecase = new Map();
    for (const card of cards) {
        const usecaseId = card[1].usecaseId;
        if (!usecaseId || !datasetNamesOf(card).length) continue;
        cardsByUsecase.set(usecaseId, [...(cardsByUsecase.get(usecaseId) || []), card]);
    }
    return [...cardsByUsecase].map(([usecaseId, owned]) => {
        const names = [...new Set(owned.flatMap(datasetNamesOf))];
        const query = names.map((name) => `dataset_name=${encodeURIComponent(name)}`).join('&');
        return { usecaseId, path: `ai-generation/jobs/${usecaseId}?${query}`, cards: owned };
    });
}

/**
 * The last ``updateGenerationProgress`` the WebSocket would have delivered for
 * each card in *cards* whose newest job, in a GET
 * ``ai-generation/jobs/{usecase_id}`` answer, has ended. Built like the live
 * message, so the card shows the job's error text and skip summary. An upload
 * card takes only a job created after its upload started, by the upload's
 * ``started_at`` in *uploadStartedAt* (epoch ms by upload status id).
 */
export function generationEventsFromJobs(cards, usecaseId, jobsResult, uploadStartedAt = {}) {
    const jobs = Array.isArray(jobsResult?.jobs) ? jobsResult.jobs : [];
    const jobByDataset = new Map(jobs.map((job) => [job.dataset_name, job]));
    const finished = new Map();
    for (const card of cards) {
        const { uploadStatusId } = card[1];
        const job = datasetNamesOf(card).map((name) => jobByDataset.get(name)).find(Boolean);
        if (!FINISHED_RUN_STATUSES.has(job?.status)) continue;
        if (uploadStatusId && !startedAfterUpload(job, uploadStartedAt[uploadStatusId])) continue;
        finished.set(job.dataset_name, job);
    }
    return [...finished.values()].map((job) => {
        const event = {
            action: 'updateGenerationProgress',
            usecaseId,
            datasetName: job.dataset_name,
            progress: job.progress ?? 0,
            status: job.status,
            totalPrompts: job.total_prompts ?? 0,
            processedPrompts: job.processed_prompts ?? 0,
            phase: job.phase,
            errorDetails: job.error_details,
            failedPrompts: job.failed_prompts ?? 0,
            failureBreakdown: job.failure_breakdown,
        };
        for (const key of GENERATION_OPTIONAL_KEYS) {
            if (job[key] != null) event[key] = job[key];
        }
        return event;
    });
}

/**
 * Retrieval Agent rows still in flight locally that the active status GET no
 * longer lists. A row whose agent already finished (now showing the dataset
 * upload) is left alone, so its completion is not announced twice.
 */
export function unlistedRetrievalAgentEntries(progressStatus, usecaseId, activeItems) {
    const activeSessions = new Set((activeItems || []).map((item) => item.sessionId));
    return listActiveRetrievalAgentEntriesForUsecase(progressStatus, usecaseId).filter(
        (entry) => !entry.agentFinished && !(entry.sessionId && activeSessions.has(entry.sessionId))
    );
}

/**
 * ``updateAgentSessionStatus`` events for *entries* whose session has a final
 * status in GET ``retrieval-agent/sessions/{usecase_id}``, matched by session
 * id, else by dataset name.
 */
export function agentEventsFromSessions(entries, usecaseId, sessionsResult) {
    const rows = sessionsOf(sessionsResult);
    return entries
        .map((entry) => {
            const row = rows.find((candidate) =>
                entry.sessionId
                    ? candidate.session_id === entry.sessionId
                    : candidate.dataset_name === entry.datasetName
            );
            if (!FINISHED_RUN_STATUSES.has(row?.status)) return null;
            const isCompleted = row.status === 'completed';
            return {
                action: 'updateAgentSessionStatus',
                usecaseId,
                sessionId: row.session_id || entry.sessionId,
                datasetName: row.dataset_name || entry.datasetName,
                status: isCompleted ? 'completed' : 'failed',
                isCompleted,
                isError: !isCompleted,
            };
        })
        .filter(Boolean);
}
